import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const fmt  = (n) => `£${parseFloat(n || 0).toFixed(2)}`;
const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316'];

// ── Reusable Modal ────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Input({ label, required, ...props }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" {...props} />
    </div>
  );
}

function Select({ label, required, children, ...props }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" {...props}>
        {children}
      </select>
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500" rows={3} {...props} />
    </div>
  );
}

function StatCard({ label, value, color = 'purple', sub }) {
  const colors = {
    purple: 'bg-purple-50  border-purple-200  text-purple-700',
    green:  'bg-green-50   border-green-200   text-green-700',
    red:    'bg-red-50     border-red-200     text-red-700',
    blue:   'bg-blue-50    border-blue-200    text-blue-700',
    yellow: 'bg-yellow-50  border-yellow-200  text-yellow-700',
    pink:   'bg-pink-50    border-pink-200    text-pink-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-sm font-medium opacity-75">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
}

// ============================================================
// TAB 1 — LEDGER MANAGEMENT
// ============================================================
function LedgerTab() {
  const [vendors, setVendors]               = useState([]);
  const [selectedVendor, setSelected]       = useState(null);
  const [ledger, setLedger]                 = useState(null);
  const [showVendorForm, setVendorForm]     = useState(false);
  const [showPurchaseForm, setPurchaseForm] = useState(false);
  const [loading, setLoading]               = useState(false);
  const [dateRange, setDateRange]           = useState({ start: monthStart(), end: today() });
  const [vendorForm, setVendorData]         = useState({ vendor_name: '', vendor_type: 'Supplier', contact_detail: '', opening_balance: 0 });
  const [purchaseForm, setPurchaseData]     = useState({ vendor_id: '', product_name: '', quantity: '', rate: '', purchase_date: today(), invoice_number: '', notes: '' });
  const [editVendor, setEditVendor]         = useState(null);

  const loadVendors = useCallback(async () => {
    const res = await api.get('/ledger/vendors');
    setVendors(res.data.vendors || []);
  }, []);

  const loadLedger = useCallback(async (vendorId) => {
    setLoading(true);
    const res = await api.get(`/ledger/ledger/${vendorId}`, { params: { start_date: dateRange.start, end_date: dateRange.end } });
    setLedger(res.data);
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { loadVendors(); }, [loadVendors]);
  useEffect(() => { if (selectedVendor) loadLedger(selectedVendor.id); }, [selectedVendor, loadLedger]);

  const saveVendor = async () => {
    if (editVendor) { await api.put(`/ledger/vendors/${editVendor.id}`, vendorForm); }
    else            { await api.post('/ledger/vendors', vendorForm); }
    setVendorForm(false); setEditVendor(null);
    setVendorData({ vendor_name: '', vendor_type: 'Supplier', contact_detail: '', opening_balance: 0 });
    loadVendors();
  };

  const deleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await api.delete(`/ledger/vendors/${id}`);
    loadVendors();
    if (selectedVendor?.id === id) setSelected(null);
  };

  const savePurchase = async () => {
    const data = { ...purchaseForm, vendor_id: selectedVendor?.id || purchaseForm.vendor_id };
    await api.post('/ledger/purchases', data);
    setPurchaseForm(false);
    setPurchaseData({ vendor_id: '', product_name: '', quantity: '', rate: '', purchase_date: today(), invoice_number: '', notes: '' });
    if (selectedVendor) loadLedger(selectedVendor.id);
    loadVendors();
  };

  const deletePurchase = async (id) => {
    if (!window.confirm('Delete this purchase?')) return;
    await api.delete(`/ledger/purchases/${id}`);
    loadLedger(selectedVendor.id);
    loadVendors();
  };

  return (
    <div className="flex gap-6">
      {/* Vendor List */}
      <div className="w-72 flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-gray-800">Vendors</h3>
          <button onClick={() => { setVendorData({ vendor_name: '', vendor_type: 'Supplier', contact_detail: '', opening_balance: 0 }); setEditVendor(null); setVendorForm(true); }}
            className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-purple-700">
            + Add Vendor
          </button>
        </div>
        <div className="space-y-2 max-h-screen overflow-y-auto">
          {vendors.map(v => (
            <div key={v.id} onClick={() => setSelected(v)}
              className={`p-3 rounded-xl border cursor-pointer transition-all ${selectedVendor?.id === v.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-800 text-sm">{v.vendor_name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${v.vendor_type === 'Supplier' ? 'bg-green-100 text-green-700' : v.vendor_type === 'Services' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                    {v.vendor_type}
                  </span>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-bold ${parseFloat(v.balance_due) > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(v.balance_due)}</p>
                  <p className="text-xs text-gray-400">due</p>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={e => { e.stopPropagation(); setVendorData(v); setEditVendor(v); setVendorForm(true); }} className="text-xs text-purple-600 hover:underline">Edit</button>
                <button onClick={e => { e.stopPropagation(); deleteVendor(v.id); }} className="text-xs text-red-600 hover:underline">Delete</button>
              </div>
            </div>
          ))}
          {vendors.length === 0 && <p className="text-gray-400 text-sm text-center py-8">No vendors yet</p>}
        </div>
      </div>

      {/* Ledger Detail */}
      <div className="flex-1">
        {!selectedVendor ? (
          <div className="flex items-center justify-center h-64 text-gray-400">
            <div className="text-center"><p className="text-5xl mb-3">📒</p><p className="text-lg">Select a vendor to view their ledger</p></div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-bold text-gray-800 text-lg">{selectedVendor.vendor_name}</h3>
                <p className="text-sm text-gray-500">{selectedVendor.contact_detail}</p>
              </div>
              <div className="flex gap-2 items-center flex-wrap justify-end">
                <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="border rounded-lg px-2 py-1 text-sm" />
                <span className="text-gray-400">to</span>
                <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="border rounded-lg px-2 py-1 text-sm" />
                <button onClick={() => { setPurchaseData(p => ({ ...p, vendor_id: selectedVendor.id })); setPurchaseForm(true); }}
                  className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
                  + Add Purchase
                </button>
              </div>
            </div>

            {ledger && (
              <>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <StatCard label="Opening Balance" value={fmt(ledger.summary?.opening_balance)} color="blue" />
                  <StatCard label="Total Purchased"  value={fmt(ledger.summary?.total_purchased)}  color="red" />
                  <StatCard label="Total Paid"       value={fmt(ledger.summary?.total_paid)}       color="green" />
                  <StatCard label="Balance Due"      value={fmt(ledger.summary?.balance_due)}
                    color={parseFloat(ledger.summary?.balance_due) > 0 ? 'red' : 'green'} />
                </div>
                <div className="bg-white rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-gray-600">Date</th>
                        <th className="text-left p-3 text-gray-600">Description</th>
                        <th className="text-right p-3 text-gray-600">Debit (Dr)</th>
                        <th className="text-right p-3 text-gray-600">Credit (Cr)</th>
                        <th className="text-right p-3 text-gray-600">Balance</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={6} className="text-center p-8 text-gray-400">Loading...</td></tr>
                      ) : ledger.entries?.map((entry, i) => (
                        <tr key={i} className={`border-b hover:bg-gray-50 ${entry.type === 'opening_balance' ? 'bg-purple-50' : ''}`}>
                          <td className="p-3 text-gray-500">{entry.date || '—'}</td>
                          <td className="p-3 text-gray-800">{entry.description}</td>
                          <td className="p-3 text-right text-red-600 font-medium">{entry.debit > 0 ? fmt(entry.debit) : '—'}</td>
                          <td className="p-3 text-right text-green-600 font-medium">{entry.credit > 0 ? fmt(entry.credit) : '—'}</td>
                          <td className={`p-3 text-right font-bold ${parseFloat(entry.balance) > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(entry.balance)}</td>
                          <td className="p-3 text-center">
                            {entry.type === 'purchase' && (
                              <button onClick={() => deletePurchase(entry.id)} className="text-xs text-red-400 hover:text-red-600">✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {showVendorForm && (
        <Modal title={editVendor ? 'Edit Vendor' : 'Add Vendor'} onClose={() => setVendorForm(false)}>
          <Input label="Vendor Name" required value={vendorForm.vendor_name} onChange={e => setVendorData(p => ({ ...p, vendor_name: e.target.value }))} />
          <Select label="Vendor Type" required value={vendorForm.vendor_type} onChange={e => setVendorData(p => ({ ...p, vendor_type: e.target.value }))}>
            <option>Supplier</option><option>Services</option><option>Other</option>
          </Select>
          <Textarea label="Contact Details" value={vendorForm.contact_detail || ''} onChange={e => setVendorData(p => ({ ...p, contact_detail: e.target.value }))} />
          <Input label="Opening Balance (£)" type="number" value={vendorForm.opening_balance} onChange={e => setVendorData(p => ({ ...p, opening_balance: e.target.value }))} />
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setVendorForm(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={saveVendor} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save</button>
          </div>
        </Modal>
      )}

      {showPurchaseForm && (
        <Modal title="Add Purchase" onClose={() => setPurchaseForm(false)}>
          <Input label="Product Name" required value={purchaseForm.product_name} onChange={e => setPurchaseData(p => ({ ...p, product_name: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Quantity" required type="number" value={purchaseForm.quantity} onChange={e => setPurchaseData(p => ({ ...p, quantity: e.target.value }))} />
            <Input label="Rate (£)" required type="number" step="0.01" value={purchaseForm.rate} onChange={e => setPurchaseData(p => ({ ...p, rate: e.target.value }))} />
          </div>
          {purchaseForm.quantity && purchaseForm.rate && (
            <p className="text-sm text-purple-600 mb-3 font-medium">Total: {fmt(purchaseForm.quantity * purchaseForm.rate)}</p>
          )}
          <Input label="Purchase Date" required type="date" value={purchaseForm.purchase_date} onChange={e => setPurchaseData(p => ({ ...p, purchase_date: e.target.value }))} />
          <Input label="Invoice Number" value={purchaseForm.invoice_number} onChange={e => setPurchaseData(p => ({ ...p, invoice_number: e.target.value }))} />
          <Textarea label="Notes" value={purchaseForm.notes} onChange={e => setPurchaseData(p => ({ ...p, notes: e.target.value }))} />
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setPurchaseForm(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={savePurchase} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Save Purchase</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// TAB 2 — TRANSACTIONS
// ============================================================
function TransactionsTab() {
  const [vendors, setVendors]     = useState([]);
  const [payments, setPayments]   = useState([]);
  const [selectedVendor, setSel]  = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [editPayment, setEdit]    = useState(null);
  const [dateRange, setDateRange] = useState({ start: monthStart(), end: today() });
  const [form, setForm]           = useState({ vendor_id: '', amount: '', payment_date: today(), payment_method: 'Cash', reference_number: '', notes: '' });

  const load = useCallback(async () => {
    const [vRes, pRes] = await Promise.all([
      api.get('/ledger/vendors'),
      api.get('/ledger/payments', { params: { vendor_id: selectedVendor || undefined, start_date: dateRange.start, end_date: dateRange.end } })
    ]);
    setVendors(vRes.data.vendors || []);
    setPayments(pRes.data.payments || []);
  }, [selectedVendor, dateRange]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (editPayment) { await api.put(`/ledger/payments/${editPayment.id}`, form); }
    else             { await api.post('/ledger/payments', form); }
    setForm({ vendor_id: '', amount: '', payment_date: today(), payment_method: 'Cash', reference_number: '', notes: '' });
    setShowForm(false); setEdit(null); load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    await api.delete(`/ledger/payments/${id}`); load();
  };

  const selectedV = vendors.find(v => v.id === parseInt(selectedVendor));

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="flex gap-3 items-center flex-wrap">
          <select value={selectedVendor} onChange={e => setSel(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Vendors</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
          </select>
          <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="border rounded-lg px-2 py-2 text-sm" />
          <span className="text-gray-400">to</span>
          <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="border rounded-lg px-2 py-2 text-sm" />
        </div>
        <button onClick={() => { setForm(p => ({ ...p, vendor_id: selectedVendor })); setEdit(null); setShowForm(true); }}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm">
          + Record Payment
        </button>
      </div>

      {selectedV && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard label="Total Purchased" value={fmt(selectedV.total_purchased)} color="red" />
          <StatCard label="Total Paid"      value={fmt(selectedV.total_paid)}      color="green" />
          <StatCard label="Balance Due"     value={fmt(selectedV.balance_due)}
            color={parseFloat(selectedV.balance_due) > 0 ? 'red' : 'green'} />
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-3 text-gray-600">Date</th>
              <th className="text-left p-3 text-gray-600">Vendor</th>
              <th className="text-left p-3 text-gray-600">Method</th>
              <th className="text-left p-3 text-gray-600">Reference</th>
              <th className="text-left p-3 text-gray-600">Notes</th>
              <th className="text-right p-3 text-gray-600">Amount</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id} className="border-b hover:bg-gray-50">
                <td className="p-3 text-gray-500">{p.payment_date?.split('T')[0]}</td>
                <td className="p-3 font-medium text-gray-800">{p.vendor_name}</td>
                <td className="p-3"><span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">{p.payment_method}</span></td>
                <td className="p-3 text-gray-500">{p.reference_number || '—'}</td>
                <td className="p-3 text-gray-500 text-xs">{p.notes || '—'}</td>
                <td className="p-3 text-right font-bold text-green-600">{fmt(p.amount)}</td>
                <td className="p-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setForm(p); setEdit(p); setShowForm(true); }} className="text-xs text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => del(p.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {payments.length === 0 && <tr><td colSpan={7} className="text-center p-8 text-gray-400">No payments recorded</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <Modal title={editPayment ? 'Edit Payment' : 'Record Payment'} onClose={() => setShowForm(false)}>
          <Select label="Vendor" required value={form.vendor_id} onChange={e => setForm(p => ({ ...p, vendor_id: e.target.value }))}>
            <option value="">Select vendor...</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.vendor_name} (Due: {fmt(v.balance_due)})</option>)}
          </Select>
          <Input label="Amount (£)" required type="number" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
          <Input label="Payment Date" required type="date" value={form.payment_date} onChange={e => setForm(p => ({ ...p, payment_date: e.target.value }))} />
          <Select label="Payment Method" value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))}>
            <option>Cash</option><option>Bank Transfer</option><option>Cheque</option><option>Other</option>
          </Select>
          <Input label="Reference Number" value={form.reference_number || ''} onChange={e => setForm(p => ({ ...p, reference_number: e.target.value }))} />
          <Textarea label="Notes" value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={save} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// TAB 3 — ACCOUNT STATEMENT (Money IN vs OUT, weekly)
// ============================================================
function AccountStatementTab() {
  const [statement, setStatement] = useState(null);
  const [expenses, setExpenses]   = useState([]);
  const [showExpForm, setExpForm] = useState(false);
  const [editExp, setEditExp]     = useState(null);
  const [dateRange, setDateRange] = useState({ start: monthStart(), end: today() });
  const [expForm, setExpData]     = useState({ category: '', description: '', amount: '', expense_date: today() });
  const [loading, setLoading]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [sRes, eRes] = await Promise.all([
      api.get('/ledger/account-statement', { params: { start_date: dateRange.start, end_date: dateRange.end } }),
      api.get('/ledger/expenses',           { params: { start_date: dateRange.start, end_date: dateRange.end } })
    ]);
    setStatement(sRes.data.statement);
    setExpenses(eRes.data.expenses || []);
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { load(); }, [load]);

  const saveExp = async () => {
    if (editExp) { await api.put(`/ledger/expenses/${editExp.id}`, expForm); }
    else         { await api.post('/ledger/expenses', expForm); }
    setExpData({ category: '', description: '', amount: '', expense_date: today() });
    setExpForm(false); setEditExp(null); load();
  };

  const delExp = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/ledger/expenses/${id}`); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="flex gap-3 items-center">
          <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="border rounded-lg px-2 py-2 text-sm" />
          <span className="text-gray-400">to</span>
          <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="border rounded-lg px-2 py-2 text-sm" />
        </div>
        <button onClick={() => { setExpData({ category: '', description: '', amount: '', expense_date: today() }); setEditExp(null); setExpForm(true); }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 text-sm">
          + Add Expense
        </button>
      </div>

      {loading ? <p className="text-center text-gray-400 py-8">Loading...</p> : statement && (
        <>
          {/* Money IN / OUT Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <p className="text-green-700 font-bold text-lg mb-3">💰 Money IN (Sales)</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">🛒 eBay Sales</span><span className="font-medium">{fmt(statement.money_in.ebay_sales)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">📦 Amazon Sales</span><span className="font-medium">{fmt(statement.money_in.amazon_sales)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">🛍️ Shopify Sales</span><span className="font-medium">{fmt(statement.money_in.shopify_sales)}</span></div>
                <div className="flex justify-between border-t pt-2 font-bold text-green-700"><span>Total IN</span><span>{fmt(statement.money_in.total)}</span></div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <p className="text-red-700 font-bold text-lg mb-3">💸 Money OUT</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Vendor Payments</span><span className="font-medium">{fmt(statement.money_out.vendor_payments)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Expenses</span><span className="font-medium">{fmt(statement.money_out.expenses)}</span></div>
                <div className="flex justify-between border-t pt-2 font-bold text-red-700"><span>Total OUT</span><span>{fmt(statement.money_out.total)}</span></div>
              </div>
            </div>

            <div className={`${parseFloat(statement.net_position) >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'} border rounded-xl p-5 flex flex-col justify-center items-center`}>
              <p className="text-gray-600 text-sm mb-1">Net Position</p>
              <p className={`text-3xl font-bold ${parseFloat(statement.net_position) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{fmt(statement.net_position)}</p>
              <p className="text-xs text-gray-400 mt-1">{parseFloat(statement.net_position) >= 0 ? '✅ Positive' : '⚠️ Negative'}</p>
            </div>
          </div>

          {/* Weekly Breakdown Chart */}
          {statement.weekly_breakdown && statement.weekly_breakdown.length > 0 && (
            <div className="bg-white rounded-xl border p-5 mb-6">
              <h4 className="font-bold text-gray-700 mb-4">📊 Weekly: Money IN vs Money OUT</h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={statement.weekly_breakdown} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week_start" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `£${v}`} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Legend />
                  <Bar dataKey="money_in"  name="Money IN"  fill="#10b981" radius={[4,4,0,0]} />
                  <Bar dataKey="money_out" name="Money OUT" fill="#ef4444" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Expenses Table */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <h4 className="font-bold text-gray-700">Expenses</h4>
              <span className="text-sm text-gray-500">Total: {fmt(expenses.reduce((s, e) => s + parseFloat(e.amount), 0))}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left p-3 text-gray-600">Date</th>
                  <th className="text-left p-3 text-gray-600">Category</th>
                  <th className="text-left p-3 text-gray-600">Description</th>
                  <th className="text-right p-3 text-gray-600">Amount</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-gray-500">{e.expense_date?.split('T')[0]}</td>
                    <td className="p-3"><span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">{e.category}</span></td>
                    <td className="p-3 text-gray-600">{e.description || '—'}</td>
                    <td className="p-3 text-right font-bold text-red-600">{fmt(e.amount)}</td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => { setExpData(e); setEditExp(e); setExpForm(true); }} className="text-xs text-blue-600 hover:underline">Edit</button>
                        <button onClick={() => delExp(e.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={5} className="text-center p-8 text-gray-400">No expenses recorded</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showExpForm && (
        <Modal title={editExp ? 'Edit Expense' : 'Add Expense'} onClose={() => setExpForm(false)}>
          <Input label="Category" required placeholder="e.g. Shipping, Marketing" value={expForm.category} onChange={e => setExpData(p => ({ ...p, category: e.target.value }))} />
          <Textarea label="Description" value={expForm.description || ''} onChange={e => setExpData(p => ({ ...p, description: e.target.value }))} />
          <Input label="Amount (£)" required type="number" step="0.01" value={expForm.amount} onChange={e => setExpData(p => ({ ...p, amount: e.target.value }))} />
          <Input label="Date" required type="date" value={expForm.expense_date} onChange={e => setExpData(p => ({ ...p, expense_date: e.target.value }))} />
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setExpForm(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={saveExp} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
// TAB 4 — FINANCIAL REPORT (full P&L + charts + Excel export)
// ============================================================
function FinancialReportTab() {
  const [report, setReport]       = useState(null);
  const [dateRange, setDateRange] = useState({ start: monthStart(), end: today() });
  const [loading, setLoading]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get('/ledger/financial-report', { params: { start_date: dateRange.start, end_date: dateRange.end } });
    setReport(res.data.report);
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { load(); }, [load]);

  // Download CSV export
  const downloadExcel = () => {
    const params = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end });
    const baseUrl = api.defaults?.baseURL || 'https://check.policypulsehq.com/api';
    // const baseUrl = api.defaults?.baseURL || 'http://localhost:5000/api';
    const token   = localStorage.getItem('token');
    // Use fetch to download with auth header
    fetch(`${baseUrl}/ledger/financial-report/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.blob())
      .then(blob => {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = `financial-report-${dateRange.start}-to-${dateRange.end}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      });
  };

  // Pie chart data — platform revenue split
  const pieData = report ? [
    { name: 'eBay',    value: parseFloat(report.stores.ebay?.revenue    || 0) },
    { name: 'Amazon',  value: parseFloat(report.stores.amazon?.revenue  || 0) },
    { name: 'Shopify', value: parseFloat(report.stores.shopify?.revenue || 0) },
  ].filter(d => d.value > 0) : [];

  return (
    <div>
      {/* Date controls + Export button */}
      <div className="flex gap-3 items-center mb-6 flex-wrap">
        <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="border rounded-lg px-2 py-2 text-sm" />
        <span className="text-gray-400">to</span>
        <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="border rounded-lg px-2 py-2 text-sm" />
        <button onClick={downloadExcel}
          className="ml-auto bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm flex items-center gap-2">
          ⬇️ Download CSV / Excel
        </button>
      </div>

      {loading ? <p className="text-center text-gray-400 py-8">Loading...</p> : report && (
        <>
          {/* ── Top KPI Cards ── */}
          <div className="grid grid-cols-5 gap-3 mb-6">
            <StatCard label="Total Revenue"  value={fmt(report.totals.total_revenue)}  color="blue" />
            <StatCard label="Total Cost"     value={fmt(report.totals.total_cost)}     color="red" />
            <StatCard label="Platform Fees"  value={fmt(report.totals.total_fees)}     color="yellow" />
            <StatCard label="Expenses"       value={fmt(report.totals.total_expenses)} color="pink" />
            <StatCard label="Net Profit"     value={fmt(report.totals.net_profit)}
              color={parseFloat(report.totals.net_profit) >= 0 ? 'green' : 'red'}
              sub={`${report.totals.profit_margin}% margin`} />
          </div>

          {/* ── Vendor Stock Summary ── */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-6">
            <p className="font-bold text-indigo-800 mb-3">🏭 Vendor Stock Summary</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-white rounded-lg p-4 border border-indigo-100">
                <p className="text-gray-500">Stock Purchased from Vendors</p>
                <p className="text-2xl font-bold text-indigo-700 mt-1">{fmt(report.vendor_summary.stock_purchased)}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-indigo-100">
                <p className="text-gray-500">Payments Made to Vendors</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{fmt(report.vendor_summary.payments_made)}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-indigo-100">
                <p className="text-gray-500">Still Owed to Vendors</p>
                <p className={`text-2xl font-bold mt-1 ${parseFloat(report.vendor_summary.still_owed) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {fmt(report.vendor_summary.still_owed)}
                </p>
              </div>
            </div>
          </div>

          {/* ── Charts Row ── */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Daily Revenue & Profit Trend */}
            <div className="bg-white rounded-xl border p-5">
              <h4 className="font-bold text-gray-700 mb-4">📈 Daily Revenue & Profit Trend</h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={report.daily_trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `£${v}`} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="total_revenue" name="Revenue" stroke="#6366f1" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="total_profit"  name="Profit"  stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Platform Revenue Pie */}
            <div className="bg-white rounded-xl border p-5">
              <h4 className="font-bold text-gray-700 mb-4">🥧 Revenue by Platform</h4>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-center py-16">No revenue data</p>}
            </div>
          </div>

          {/* Revenue by Platform per day bar chart */}
          {report.daily_trend && report.daily_trend.length > 0 && (
            <div className="bg-white rounded-xl border p-5 mb-6">
              <h4 className="font-bold text-gray-700 mb-4">📊 Daily Revenue by Platform</h4>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={report.daily_trend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `£${v}`} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Legend />
                  <Bar dataKey="ebay"    name="eBay"    stackId="a" fill="#6366f1" />
                  <Bar dataKey="amazon"  name="Amazon"  stackId="a" fill="#f59e0b" />
                  <Bar dataKey="shopify" name="Shopify" stackId="a" fill="#10b981" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── Store Breakdown Table ── */}
          <div className="bg-white rounded-xl border overflow-hidden mb-6">
            <div className="p-4 border-b bg-gray-50"><h4 className="font-bold text-gray-700">Store Breakdown</h4></div>
            <table className="w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left p-3 text-gray-600">Store</th>
                  <th className="text-right p-3 text-gray-600">Orders</th>
                  <th className="text-right p-3 text-gray-600">Revenue</th>
                  <th className="text-right p-3 text-gray-600">Cost</th>
                  <th className="text-right p-3 text-gray-600">Fees</th>
                  <th className="text-right p-3 text-gray-600">Profit</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'eBay',    icon: '🛒', data: report.stores.ebay    },
                  { name: 'Amazon',  icon: '📦', data: report.stores.amazon  },
                  { name: 'Shopify', icon: '🛍️', data: report.stores.shopify },
                ].map(({ name, icon, data }) => (
                  <tr key={name} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-800">{icon} {name}</td>
                    <td className="p-3 text-right text-gray-600">{data?.orders || 0}</td>
                    <td className="p-3 text-right font-medium">{fmt(data?.revenue)}</td>
                    <td className="p-3 text-right text-red-500">{fmt(data?.cost)}</td>
                    <td className="p-3 text-right text-orange-500">{fmt(data?.fees)}</td>
                    <td className={`p-3 text-right font-bold ${parseFloat(data?.profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(data?.profit)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-bold border-t-2">
                  <td className="p-3">Total</td>
                  <td className="p-3 text-right">{(report.stores.ebay?.orders || 0) + (report.stores.amazon?.orders || 0) + (report.stores.shopify?.orders || 0)}</td>
                  <td className="p-3 text-right">{fmt(report.totals.total_revenue)}</td>
                  <td className="p-3 text-right text-red-600">{fmt(report.totals.total_cost)}</td>
                  <td className="p-3 text-right text-orange-600">{fmt(report.totals.total_fees)}</td>
                  <td className={`p-3 text-right ${parseFloat(report.totals.gross_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(report.totals.gross_profit)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── P&L Summary ── */}
          <div className="bg-white rounded-xl border p-6 max-w-sm">
            <h4 className="font-bold text-gray-700 mb-4">📊 Profit & Loss</h4>
            <div className="space-y-3">
              {[
                { label: 'Total Revenue',          value:  report.totals.total_revenue,   color: 'text-blue-600' },
                { label: '(-) Cost of Goods',      value: -report.totals.total_cost,      color: 'text-red-600' },
                { label: '(-) Platform Fees',      value: -report.totals.total_fees,      color: 'text-orange-600' },
                { label: 'Gross Profit',            value:  report.totals.gross_profit,    color: 'text-gray-800', bold: true, border: true },
                { label: '(-) Expenses',            value: -report.totals.total_expenses,  color: 'text-pink-600' },
                { label: 'Net Profit',              value:  report.totals.net_profit,      color: parseFloat(report.totals.net_profit) >= 0 ? 'text-green-600' : 'text-red-600', bold: true, border: true, large: true },
              ].map((row, i) => (
                <div key={i} className={`flex justify-between items-center ${row.border ? 'border-t pt-3' : ''}`}>
                  <span className={`${row.bold ? 'font-bold' : 'text-gray-600 text-sm'} ${row.large ? 'text-base' : ''}`}>{row.label}</span>
                  <span className={`${row.color} ${row.bold ? 'font-bold' : ''} ${row.large ? 'text-lg' : 'text-sm'}`}>
                    {row.value < 0 ? `-${fmt(Math.abs(row.value))}` : fmt(row.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const TAB_MAP = { ledger: 0, transactions: 1, statement: 2, reports: 3 };
const TAB_TITLES    = ['Ledger Management', 'Transaction Recording', 'Account Statement', 'Financial Report'];
const TAB_SUBTITLES = [
  'Vendor purchases and running balances',
  'Payments made to vendors',
  'Money in vs money out — weekly breakdown',
  'Full P&L across eBay, Amazon & Shopify with charts'
];

export default function LedgerDashboard({ activeTab = 'ledger' }) {
  const tabIndex = TAB_MAP[activeTab] ?? 0;

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{TAB_TITLES[tabIndex]}</h1>
          <p className="text-gray-500 mt-1">{TAB_SUBTITLES[tabIndex]}</p>
        </div>
        <div className="bg-white rounded-2xl border p-6 min-h-96">
          {tabIndex === 0 && <LedgerTab />}
          {tabIndex === 1 && <TransactionsTab />}
          {tabIndex === 2 && <AccountStatementTab />}
          {tabIndex === 3 && <FinancialReportTab />}
        </div>
      </div>
    </div>
  );
}